import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Chip, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  ListField,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { formatBytes, formatRelative } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import { automationGw, type Agent, type AutomationGwApi, type Route, type RouteInput } from '../api/client';
import { useAutomationTarget, useAutomationWrite } from '../shared';
import '../automation.css';

const EMPTY: RouteInput = {
  clientId: '',
  publicPort: 0,
  targetHost: '',
  targetPort: 0,
  enabled: true,
  allowedNamespaces: [],
  direction: 'forward',
  allowedSourceIPs: [],
  channelInactivityTimeoutSeconds: null,
  sendProxyProtocol: false,
};

const toInput = (r: Route): RouteInput => {
  const { id: _id, ...rest } = r;
  return { ...EMPTY, ...rest };
};

/**
 * Tunnels per agent. Forward: pods connect to the gateway's port and reach a warehouse host through the agent.
 * Reverse: the agent listens in the warehouse and tunnels to a GCP service.
 */
export function RoutesPage() {
  const target = useAutomationTarget();
  const api = useMemo(() => automationGw(target), [target]);
  const canWrite = useAutomationWrite();
  const toast = useNotice();
  const [agent, setAgent] = useParam('agent', '');
  const [direction, setDirection] = useParam('direction', '');
  const data = useAsync(async () => {
    const [routes, agents, tunnels] = await Promise.all([api.routes(), api.agents(), api.tunnels()]);
    return { routes, agents, tunnels };
  }, [api]);
  const [editing, setEditing] = useState<{ id: string | null; draft: RouteInput } | null>(null);

  const d = data.data;
  const agentOf = (id: string) => d?.agents.find((a) => a.id === id);
  const live = (routeId: string) => d?.tunnels.flatMap((t) => t.routes).find((r) => r.routeId === routeId);
  const connected = (agentId: string) => !!d?.tunnels.find((t) => t.clientDbId === agentId)?.isConnected;
  const rows = (d?.routes ?? [])
    .filter((r) => (!agent || r.clientId === agent) && (!direction || r.direction === direction))
    .sort(
      (a, b) =>
        (agentOf(a.clientId)?.displayName ?? '').localeCompare(agentOf(b.clientId)?.displayName ?? '') ||
        a.publicPort - b.publicPort
    );

  const act = async (what: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(what);
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="Routes"
      description="TCP tunnels through the agents. Forward: pods reach a warehouse host via the gateway's port. Reverse: warehouse devices reach a GCP service via the agent's port."
      actions={
        <>
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={data.loading}
            onClick={() => void data.reload()}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button size="sm" onClick={() => setEditing({ id: null, draft: { ...EMPTY, clientId: agent } })}>
              New route
            </Button>
          )}
        </>
      }
    >
      {toast.element}
      <div className="ag-toolbar">
        <Select
          aria-label="Agent"
          value={agent}
          onChange={setAgent}
          options={[
            { value: '', label: `All agents (${d?.agents.length ?? 0})` },
            ...(d?.agents ?? [])
              .slice()
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((a) => ({ value: a.id, label: `${a.displayName} (${a.clientId})` })),
          ]}
        />
        <Select
          aria-label="Direction"
          value={direction}
          onChange={setDirection}
          options={[
            { value: '', label: 'Both directions' },
            { value: 'forward', label: 'Forward' },
            { value: 'reverse', label: 'Reverse' },
          ]}
        />
      </div>
      <ErrorBox error={data.error} />
      <div className="nyk-card">
        <Loading loading={data.loading && !d} />
        {!!rows.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="ag-routes">
              <thead>
                <tr>
                  <th>Route</th>
                  <th>Agent</th>
                  <th>Direction</th>
                  <th>Restrictions</th>
                  <th className="nyk-ui-num">Channels</th>
                  <th className="nyk-ui-num">Traffic</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const a = agentOf(r.clientId);
                  const m = live(r.id);
                  return (
                    <tr key={r.id} data-route={r.id}>
                      <td className="nyk-card-title">
                        <b>
                          :{r.publicPort} → {r.targetHost}:{r.targetPort}
                        </b>
                        {!r.enabled && (
                          <>
                            {' '}
                            <Chip size="sm">disabled</Chip>
                          </>
                        )}
                      </td>
                      <td data-label="Agent">
                        {a?.displayName ?? r.clientId}
                        {a && !connected(a.id) && <div className="nyk-ui-hint nyk-text-red">offline</div>}
                      </td>
                      <td data-label="Direction">
                        {r.direction === 'reverse' ? 'Reverse' : 'Forward'}
                        {r.sendProxyProtocol && <div className="nyk-ui-hint">PROXY protocol</div>}
                      </td>
                      <td data-label="Restrictions">
                        {[
                          r.allowedNamespaces.length ? `namespaces ${r.allowedNamespaces.join(', ')}` : '',
                          r.allowedSourceIPs.length ? `sources ${r.allowedSourceIPs.join(', ')}` : '',
                          r.channelInactivityTimeoutSeconds
                            ? `idle ${r.channelInactivityTimeoutSeconds} s`
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </td>
                      <td className="nyk-ui-num" data-label="Channels">
                        {m?.activeChannels ?? 0}
                      </td>
                      <td className="nyk-ui-num" data-label="Traffic">
                        {m ? `${formatBytes(m.bytesToAgent)} ↑ ${formatBytes(m.bytesFromAgent)} ↓` : '—'}
                        {m?.lastActivity && (
                          <div className="nyk-ui-hint">{formatRelative(m.lastActivity)}</div>
                        )}
                      </td>
                      <td className="nyk-row-actions">
                        {canWrite && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditing({ id: r.id, draft: toInput(r) })}
                            >
                              Edit
                            </Button>
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Delete the route :${r.publicPort} → ${r.targetHost}:${r.targetPort}? Open connections drop.`}
                              onConfirm={() => void act('Route deleted', () => api.deleteRoute(r.id))}
                            >
                              Delete
                            </ConfirmButton>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        )}
        {d && !rows.length && <EmptyState title="No routes." />}
        <p className="nyk-ui-hint">Traffic counts since the gateway pod started.</p>
      </div>
      {editing && d && (
        <RouteDialog
          api={api}
          id={editing.id}
          initial={editing.draft}
          agents={d.agents}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success('Route saved');
            void data.reload();
          }}
        />
      )}
    </Page>
  );
}

function RouteDialog({
  api,
  id,
  initial,
  agents,
  onClose,
  onSaved,
}: {
  api: AutomationGwApi;
  id: string | null;
  initial: RouteInput;
  agents: Agent[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const port = (v: number) => Number.isInteger(v) && v >= 1 && v <= 65535;
  const save = async () => {
    if (!draft.clientId) return setError('Pick an agent.');
    if (!port(draft.publicPort) || !port(draft.targetPort)) return setError('Ports are 1–65535.');
    if (!draft.targetHost.trim()) return setError('Target host is required.');
    setBusy(true);
    setError(null);
    try {
      const body = { ...draft, targetHost: draft.targetHost.trim() };
      if (id) await api.updateRoute(id, body);
      else await api.createRoute(body);
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const reverse = draft.direction === 'reverse';
  const num = (v: string) => (v.trim() === '' ? 0 : Number(v));
  return (
    <Dialog
      open
      onClose={onClose}
      title={id ? 'Edit route' : 'New route'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="ag-form">
        <ErrorBox error={error} />
        <Select
          label="Agent"
          value={draft.clientId}
          onChange={(v) => setDraft({ ...draft, clientId: v })}
          options={[
            { value: '', label: 'Pick an agent' },
            ...agents.map((a) => ({ value: a.id, label: `${a.displayName} (${a.clientId})` })),
          ]}
        />
        <Select
          label="Direction"
          value={draft.direction}
          onChange={(v) => setDraft({ ...draft, direction: v as RouteInput['direction'] })}
          options={[
            { value: 'forward', label: 'Forward — pods → gateway port → warehouse host' },
            { value: 'reverse', label: 'Reverse — warehouse device → agent port → GCP service' },
          ]}
        />
        <div className="ag-form-row">
          <TextField
            label={reverse ? 'Agent port' : 'Gateway port'}
            type="number"
            inputMode="numeric"
            value={draft.publicPort || ''}
            onChange={(e) => setDraft({ ...draft, publicPort: num(e.target.value) })}
          />
          <TextField
            label={reverse ? 'GCP host' : 'Warehouse host'}
            value={draft.targetHost}
            onChange={(e) => setDraft({ ...draft, targetHost: e.target.value })}
          />
          <TextField
            label="Target port"
            type="number"
            inputMode="numeric"
            value={draft.targetPort || ''}
            onChange={(e) => setDraft({ ...draft, targetPort: num(e.target.value) })}
          />
        </div>
        {!reverse && (
          <ListField
            label="Allowed namespaces"
            hint="Kubernetes namespaces whose pods may use the port; empty = any."
            value={draft.allowedNamespaces}
            onChange={(v) => setDraft({ ...draft, allowedNamespaces: v })}
          />
        )}
        {reverse && (
          <ListField
            label="Allowed source IPs"
            hint="Warehouse devices that may connect to the agent's port; empty = any."
            value={draft.allowedSourceIPs}
            onChange={(v) => setDraft({ ...draft, allowedSourceIPs: v })}
          />
        )}
        <TextField
          label="Idle timeout (s)"
          hint="Close a channel without traffic after this long; empty = never."
          type="number"
          inputMode="numeric"
          value={draft.channelInactivityTimeoutSeconds ?? ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              channelInactivityTimeoutSeconds: e.target.value.trim() === '' ? null : Number(e.target.value),
            })
          }
        />
        {reverse && (
          <Switch
            checked={!!draft.sendProxyProtocol}
            onChange={(v) => setDraft({ ...draft, sendProxyProtocol: v })}
            label="Send PROXY protocol (the target must expect it)"
          />
        )}
        <Switch
          checked={draft.enabled}
          onChange={(v) => setDraft({ ...draft, enabled: v })}
          label="Enabled"
        />
      </div>
    </Dialog>
  );
}
